import type { CostReportClusterSummaryNormalized } from '@cast/shared-cost-data-access';

export type ClusterDashboardSummary = CostReportClusterSummaryNormalized & {
  gpuIdleTotal?: { toNumber(): number };
  gpuNotUsedTotal?: { toNumber(): number };
};

export type NumericSummary = Record<
  Exclude<keyof ClusterDashboardSummary, 'clusterId'>,
  number
>;

type ComputeResourceChartData = {
  provisioned: number;
  requested: number;
  requestedPercentage: number;
  used: number;
  usedPercentage: number;
  allocatable: number;
  allocatablePercentage: number;
  overhead: number;
  overheadPercentage: number;
};

export type CpuChartData = ComputeResourceChartData;
export type MemoryChartData = ComputeResourceChartData;

export type GpuChartData = {
  provisioned: number;
  requested: number;
  requestedPercentage: number;
  allocatable: number;
  allocatablePercentage: number;
  notUsed: number;
  notUsedPercentage: number;
  used: number;
  usedPercentage: number;
  idle: number;
  idlePercentage: number;
};

export type StorageChartData = {
  provisioned: number;
  requested: number;
  requestedPercentage: number;
};

const toNumber = (value: unknown): number => {
  if (value == null) {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return Number(value) || 0;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toNumber' in value &&
    typeof value.toNumber === 'function'
  ) {
    return value.toNumber();
  }
  return 0;
};

export const toNumericSummary = (
  summary: ClusterDashboardSummary
): NumericSummary =>
  Object.fromEntries(
    Object.keys(summary)
      .filter((key) => key !== 'clusterId')
      .map((key) => [
        key,
        toNumber(summary[key as keyof ClusterDashboardSummary]),
      ])
  ) as NumericSummary;

const toPercentage =
  (provisioned: number) =>
  (x: number): number =>
    provisioned ? (x * 100) / provisioned : 0;

export const getCpuChartData = ({
  cpuProvisionedTotal: provisioned,
  cpuRequestedTotal: requested,
  cpuUsed: used,
  cpuAllocatableTotal: allocatable,
}: NumericSummary): CpuChartData => {
  const getPercentage = toPercentage(provisioned);
  const overhead = provisioned - allocatable;
  return {
    provisioned,
    requested,
    requestedPercentage: getPercentage(requested),
    used,
    usedPercentage: getPercentage(used),
    allocatable,
    allocatablePercentage: getPercentage(allocatable),
    overhead,
    overheadPercentage: getPercentage(overhead),
  };
};

export const getMemoryChartData = ({
  ramProvisionedTotal: provisioned,
  ramRequestedTotal: requested,
  ramUsed: used,
  ramAllocatableTotal: allocatable,
}: NumericSummary): MemoryChartData => {
  const getPercentage = toPercentage(provisioned);
  const overhead = provisioned - allocatable;
  return {
    provisioned,
    requested,
    requestedPercentage: getPercentage(requested),
    used,
    usedPercentage: getPercentage(used),
    allocatable,
    allocatablePercentage: getPercentage(allocatable),
    overhead,
    overheadPercentage: getPercentage(overhead),
  };
};
